import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { animate, createScope, onScroll, stagger, spring, svg, utils } from "animejs";
import {
  Heart,
  Users,
  Gem,
  Gift,
  Globe,
  Scan,
  ArrowRight,
  Radio,
} from "lucide-react";
// Bead rain images
import beadHotpink from "../assets/beads/bead-hotpink.png";
import beadCyan from "../assets/beads/bead-cyan.png";
import beadPurple from "../assets/beads/bead-purple.png";
import beadGold from "../assets/beads/bead-gold.png";
import beadLime from "../assets/beads/bead-lime.png";
import beadOrange from "../assets/beads/bead-orange.png";
import beadStarPink from "../assets/beads/bead-star-pink.png";
import beadHeartRed from "../assets/beads/bead-heart-red.png";
import beadWhite from "../assets/beads/bead-white.png";
import beadBlue from "../assets/beads/bead-blue.png";
import beadFlame from "../assets/beads/bead-flame.png";
import beadTeddy from "../assets/beads/bead-teddy.png";
import beadCandy from "../assets/beads/bead-candy.png";
import beadLightning from "../assets/beads/bead-lightning.png";
import beadMushroom from "../assets/beads/bead-mushroom.png";
import beadMoon from "../assets/beads/bead-moon.png";
import beadButterfly from "../assets/beads/bead-butterfly.png";
import beadSkull from "../assets/beads/bead-skull.png";
import beadAlien from "../assets/beads/bead-alien.png";
import beadDiamond from "../assets/beads/bead-diamond.png";

const BEAD_IMAGES = [
  beadHotpink, beadCyan, beadPurple, beadGold, beadLime,
  beadOrange, beadStarPink, beadHeartRed, beadWhite, beadBlue,
  beadFlame, beadTeddy, beadCandy, beadLightning, beadMushroom,
  beadMoon, beadButterfly, beadSkull, beadAlien, beadDiamond,
];

// ─────────────────────────────────────────────────────────────────────────────
// EDM Theme configurations — PRESERVED
// ─────────────────────────────────────────────────────────────────────────────
const EDM_THEMES = {
  house: {
    name: "House",
    orbColors: [
      { color: "rgba(168, 85, 247, 0.3)", blur: "blur-3xl" },
      { color: "rgba(236, 72, 153, 0.4)", blur: "blur-3xl" },
      { color: "rgba(34, 211, 238, 0.35)", blur: "blur-3xl" },
      { color: "rgba(250, 204, 21, 0.25)", blur: "blur-3xl" },
    ],
    animationSpeed: 1.0,
    intensity: 1.0,
  },
  trance: {
    name: "Trance",
    orbColors: [
      { color: "rgba(168, 85, 247, 0.4)", blur: "blur-3xl" },
      { color: "rgba(34, 211, 238, 0.45)", blur: "blur-3xl" },
      { color: "rgba(236, 72, 153, 0.35)", blur: "blur-3xl" },
      { color: "rgba(139, 92, 246, 0.3)", blur: "blur-3xl" },
    ],
    animationSpeed: 0.7,
    intensity: 1.3,
  },
  dnb: {
    name: "Drum & Bass",
    orbColors: [
      { color: "rgba(255, 0, 110, 0.45)", blur: "blur-2xl" },
      { color: "rgba(0, 255, 0, 0.35)", blur: "blur-2xl" },
      { color: "rgba(255, 165, 0, 0.4)", blur: "blur-2xl" },
      { color: "rgba(255, 68, 0, 0.35)", blur: "blur-2xl" },
    ],
    animationSpeed: 0.4,
    intensity: 1.6,
  },
  dubstep: {
    name: "Dubstep",
    orbColors: [
      { color: "rgba(139, 0, 255, 0.5)", blur: "blur-3xl" },
      { color: "rgba(255, 69, 0, 0.45)", blur: "blur-3xl" },
      { color: "rgba(0, 255, 255, 0.4)", blur: "blur-3xl" },
      { color: "rgba(0, 0, 0, 0.6)", blur: "blur-3xl" },
    ],
    animationSpeed: 1.3,
    intensity: 1.4,
  },
  techno: {
    name: "Techno",
    orbColors: [
      { color: "rgba(0, 0, 0, 0.5)", blur: "blur-3xl" },
      { color: "rgba(255, 0, 0, 0.5)", blur: "blur-3xl" },
      { color: "rgba(255, 255, 255, 0.3)", blur: "blur-3xl" },
      { color: "rgba(128, 128, 128, 0.4)", blur: "blur-3xl" },
    ],
    animationSpeed: 0.8,
    intensity: 1.2,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Misc constants
// ─────────────────────────────────────────────────────────────────────────────
const STATS = [
  { value: "12,847", label: "Bracelets Registered", color: "#b44fff" },
  { value: "3,291",  label: "Ravers Connected",     color: "#ff3cac" },
  { value: "47",     label: "Festivals & Events",   color: "#00f5ff" },
  { value: "23",     label: "Countries Reached",    color: "#ffd700" },
];

// Numeric form for the anime.js count-up ("12,847" → 12847)
const statNumber = (value) => parseInt(value.replace(/,/g, ""), 10);

const EXPERIENCE_STEPS = [
  {
    icon: <Gem style={{ width: "2.5rem", height: "2.5rem", color: "#fff" }} />,
    title: "Create Your Kandi",
    desc:  "String your bracelet and register it with NFC or QR. Name it. Give it color, meaning, and a soul.",
    color: "#b44fff",
    gradient: "linear-gradient(135deg, #7b2fff, #b44fff, #ff3cac)",
  },
  {
    icon: <Heart style={{ width: "2.5rem", height: "2.5rem", color: "#fff" }} />,
    title: "Trade & Connect",
    desc:  "PLUR trade at a festival — both of you scan. The bracelet's story updates instantly.",
    color: "#ff3cac",
    gradient: "linear-gradient(135deg, #ff3cac, #ff6b6b, #ffd700)",
  },
  {
    icon: <Globe style={{ width: "2.5rem", height: "2.5rem", color: "#fff" }} />,
    title: "Track the Journey",
    desc:  "Watch your creation travel the world. Every scan adds a chapter. A living map of moments.",
    color: "#00f5ff",
    gradient: "linear-gradient(135deg, #00f5ff, #54a0ff, #b44fff)",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function BeadRain() {
  // Fewer falling beads on phones — each drop is its own animated layer,
  // and 35 of them visibly drops the frame rate on mid-range mobiles
  const [drops] = useState(() => {
    const count = typeof window !== "undefined" && window.innerWidth < 640 ? 16 : 35;
    return Array.from({ length: count }, (_, i) => ({
      img:      BEAD_IMAGES[i % BEAD_IMAGES.length],
      x:        Math.random() * 100,
      size:     20 + Math.random() * 28,
      delay:    Math.random() * 12,
      duration: 8 + Math.random() * 10,
      rotate:   Math.random() * 360,
      spin:     (Math.random() - 0.5) * 120,
      opacity:  0.25 + Math.random() * 0.45,
    }));
  });

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: -1 }}>
      {drops.map((d, i) => (
        <div
          key={i}
          className="absolute animate-bead-rain"
          style={{
            left:              `${d.x}%`,
            top:               `-60px`,
            animationDelay:    `${d.delay}s`,
            animationDuration: `${d.duration}s`,
            opacity:           d.opacity,
          }}
        >
          <img
            src={d.img}
            alt=""
            style={{
              width:     d.size,
              height:    d.size,
              objectFit: "contain",
              transform: `rotate(${d.rotate}deg)`,
              filter:    "drop-shadow(0 0 6px rgba(180,79,255,0.3))",
                          }}
          />
        </div>
      ))}
    </div>
  );
}

function ScanRing() {
  return (
    <div className="relative w-28 h-28 flex items-center justify-center mx-auto mb-10">
      {[0, 0.6, 1.2].map((delay, i) => (
        <div
          key={i}
          className="absolute inset-0 rounded-full animate-scan-ring"
          style={{
            border:         `2px solid ${["#00f5ff", "#b44fff", "#ff3cac"][i]}60`,
            animationDelay: `${delay}s`,
          }}
        />
      ))}
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, #b44fff, #00f5ff)",
          boxShadow:  "0 0 40px rgba(180,79,255,0.7), 0 0 80px rgba(0,245,255,0.3)",
        }}
      >
        <Scan style={{ width: "2rem", height: "2rem", color: "#fff" }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Unique-per-load title gradient
// ─────────────────────────────────────────────────────────────────────────────

function generateRaveGradient() {
  // Pick a random base hue, then spread 6 stops across a 120° slice
  // so each load has a distinct color *mood*, not a full rainbow
  const baseHue = Math.random() * 360;
  const spread = 100 + Math.random() * 40; // 100-140° arc
  const stops = 6;
  const colors = [];
  for (let i = 0; i < stops; i++) {
    const t = i / (stops - 1); // 0 → 1
    const hue = (baseHue + t * spread + (Math.random() * 20 - 10)) % 360;
    const sat = 88 + Math.random() * 12;
    const lit = 52 + Math.random() * 18;
    colors.push(`hsl(${hue}, ${sat}%, ${lit}%)`);
  }
  // mirror back for seamless loop animation
  colors.push(colors[3], colors[1], colors[0]);
  return colors;
}

function TitleBlock() {
  const colors = useMemo(() => generateRaveGradient(), []);
  const glowA = colors[0];
  const glowB = colors[2];
  const glowC = colors[4];

  return (
    <div className="relative mb-6 anim-hero-title">
      <div
        className="absolute inset-0 animate-title-glow"
        style={{
          background: `radial-gradient(ellipse at 50% 50%, ${glowA}66 0%, ${glowB}33 40%, ${glowC}22 65%, transparent 85%)`,
          filter: "blur(50px)",
          transform: "scale(1.3)",
        }}
      />
      <h1
        className="relative leading-none tracking-tight"
        style={{
          fontFamily: "var(--font-display)", fontWeight: 700,
          fontSize: "clamp(4rem, 14vw, 10rem)",
          color: glowA,
          textShadow: `0 0 50px ${glowB}90, 0 0 120px ${glowA}50`,
        }}
      >
        Kandi<br />Konnect
      </h1>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
function LandingPage({ theme = "house" }) {
  const navigate    = useNavigate();
  const activeTheme = EDM_THEMES[theme] || EDM_THEMES.house;
  const [activeStep, setActiveStep] = useState(0);
  const rootRef     = useRef(null);
  const scopeRef    = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setActiveStep((p) => (p + 1) % 3), 2800);
    return () => clearInterval(id);
  }, []);

  // ── anime.js — entrances, scroll reveals, count-ups, arc drawing ──────────
  // Everything here is additive: elements render visible by default, and on
  // completion inline styles are cleared so CSS hovers/transitions take over.
  useEffect(() => {
    scopeRef.current = createScope({
      root: rootRef,
      mediaQueries: { reduceMotion: "(prefers-reduced-motion: reduce)" },
    }).add((self) => {
      if (self.matches.reduceMotion) return; // static page for reduced-motion users

      // Reveal only after the target is 140px into the viewport — the default
      // threshold (target top touches viewport bottom) is already true at
      // scroll 0 for the section right below the 100vh hero, which made its
      // reveal play on load, offscreen.
      const REVEAL_ENTER = "bottom-=140 top";

      // Entrance helper: suspends CSS transitions while animating (they fight
      // per-frame updates), then clears inline styles so hover effects and
      // React-managed transforms win again after the entrance finishes.
      const entrance = (selector, keyframes, { trigger, delay = 0, ease = "out(3)", duration = 600 } = {}) => {
        const els = utils.$(selector);
        if (!els.length) return;
        els.forEach((el) => { el.style.transition = "none"; });
        animate(els, {
          ...keyframes,
          duration,
          ease,
          delay,
          ...(trigger ? { autoplay: onScroll({ target: trigger, enter: REVEAL_ENTER }) } : {}),
          onComplete: () => els.forEach((el) => {
            el.style.transition = "";
            el.style.transform = "";
            el.style.opacity = "";
          }),
        });
      };

      // ── Hero: one-time load sequence ──────────────────────────────────────
      entrance(".anim-hero-badge", { opacity: [0, 1], translateY: [16, 0] });
      entrance(".anim-hero-title", { opacity: [0, 1], scale: [0.92, 1] },
        { delay: 150, ease: spring({ stiffness: 120, damping: 14 }) });
      entrance(".anim-hero-tag", { opacity: [0, 1], translateY: [14, 0] },
        { delay: stagger(130, { start: 350 }) });
      entrance(".anim-hero-cta", { opacity: [0, 1], translateY: [18, 0] },
        { delay: stagger(110, { start: 550 }), ease: spring({ stiffness: 160, damping: 13 }) });
      entrance(".anim-stat-pill", { opacity: [0, 1], translateY: [10, 0] },
        { delay: stagger(90, { start: 750 }) });

      // ── Count-ups: numbers tick from 0 when they scroll into view ────────
      utils.$("[data-countup]").forEach((el) => {
        const target = parseInt(el.dataset.countup, 10);
        if (isNaN(target)) return;
        const counter = { v: 0 };
        animate(counter, {
          v: target,
          duration: 1600,
          ease: "out(4)",
          autoplay: onScroll({ target: el, enter: "bottom-=60 top" }),
          onUpdate: () => { el.textContent = Math.round(counter.v).toLocaleString("en-US"); },
        });
      });

      // ── Experience section: heading + step cards reveal on scroll ────────
      entrance(".anim-exp-head", { opacity: [0, 1], translateY: [26, 0] },
        { trigger: "#experience", duration: 700 });
      entrance(".anim-step-card", { opacity: [0, 1] },
        { trigger: "#experience", delay: stagger(140, { start: 200 }), duration: 550 });

      // ── PLUR section: word-by-word heading, arcs draw, bubbles pop ───────
      entrance(".anim-plur-head", { opacity: [0, 1], translateY: [16, 0] }, { trigger: "#plur" });
      entrance(".anim-plur-word", { opacity: [0, 1], translateY: [24, 0] },
        { trigger: "#plur", delay: stagger(70, { start: 150 }), duration: 500 });

      // ── PLUR constellation — a tight three-beat sequence (~1.8s total):
      //    1. bubbles spawn at the Radio nexus and snap outward to their corners
      //    2. the connecting arcs draw themselves between the landed bubbles
      //    3. rim beads pop in on each bubble's outer side
      const bubbles = utils.$(".anim-plur-bubble");
      const plurField = utils.$(".plur-bubbles")[0];
      const rimBeads = utils.$(".plur-bead-pop");

      if (bubbles.length && plurField) {
        bubbles.forEach((el) => { el.style.transition = "none"; });
        // Beads render visible by default (static decoration if JS fails or
        // motion is reduced); hidden here only because the reveal will run.
        utils.set(rimBeads, { opacity: 0 });

        const popBeads = () => {
          animate(rimBeads, {
            opacity: [0, 1],
            scale: [0, 1],
            delay: stagger(50),
            ease: spring({ stiffness: 260, damping: 17 }),
            onComplete: () => rimBeads.forEach((b) => {
              b.style.opacity = "";
              b.style.transform = "";
            }),
          });
        };

        const arcs = animate(svg.createDrawable(".anim-plur-arc"), {
          duration: 440,
          delay: stagger(60),
          draw: ["0 0", "0 1"],
          ease: "out(3)",
          autoplay: false,
          onComplete: popBeads,
        });

        // Center-to-corner deltas, measured per bubble (scroll-independent)
        const fieldRect = plurField.getBoundingClientRect();
        const fromCenter = (el, axis) => {
          const b = el.getBoundingClientRect();
          return axis === "x"
            ? fieldRect.left + fieldRect.width / 2 - (b.left + b.width / 2)
            : fieldRect.top + fieldRect.height / 2 - (b.top + b.height / 2);
        };

        animate(bubbles, {
          translateX: (el) => [fromCenter(el, "x"), 0],
          translateY: (el) => [fromCenter(el, "y"), 0],
          scale: [0.15, 1],
          opacity: [0, 1],
          delay: stagger(65),
          ease: spring({ stiffness: 230, damping: 20 }),
          autoplay: onScroll({ target: ".plur-bubbles", enter: REVEAL_ENTER }),
          // Overlap the beats: bubbles are visually landed well before the
          // spring's settle-tail finishes, so the arcs start on a fixed beat
          onBegin: () => { setTimeout(() => arcs.play(), 500); },
          onComplete: () => {
            bubbles.forEach((el) => {
              el.style.transition = "";
              el.style.transform = "";
              el.style.opacity = "";
            });
          },
        });
      }

      // ── Community stats grid ──────────────────────────────────────────────
      entrance(".anim-stat-tile", { opacity: [0, 1], translateY: [20, 0] },
        { trigger: ".anim-stats-grid", delay: stagger(90), duration: 550 });
    });

    return () => scopeRef.current?.revert();
  }, []);

  return (
    <div id="landing" ref={rootRef} className="min-h-screen relative overflow-hidden" style={{ background: "#0a0a0f" }}>

      {/* ── AMBIENT ROOM WASH — venue lighting, not floating orbs; tinted per
             EDM_THEMES genre so the "house/trance/dnb/…" system stays live ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 50% 0%, ${activeTheme.orbColors[0].color} 0%, transparent 65%)`,
            opacity: activeTheme.intensity,
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(180,79,255,0.14) 0%, transparent 65%)" }}
        />
      </div>

      {/* ════════════════════════════════════════════════════════════════════
           §1  HERO
      ════════════════════════════════════════════════════════════════════ */}
      <section
        id="hero"
        className="relative flex flex-col items-center justify-center min-h-screen px-4 py-24 text-center overflow-hidden"
        style={{ zIndex: 10 }}
      >
        {/* Bead rain — photorealistic beads falling in background */}
        <BeadRain />

        <ScanRing />

        {/* PLUR badge */}
        <div
          className="anim-hero-badge inline-block mb-5 px-5 py-2 rounded-full text-sm font-bold tracking-widest uppercase"
          style={{
            background: "rgba(180,79,255,0.12)",
            border:     "1px solid rgba(180,79,255,0.4)",
            color:      "#b44fff",
          }}
        >
          PLUR Culture · Digital Life
        </div>

        {/* Headline — unique gradient per load */}
        <TitleBlock />

        <p className="anim-hero-tag text-xl md:text-2xl text-white/80 mb-3 max-w-2xl leading-relaxed">
          Every bracelet holds a story.
        </p>
        <p className="anim-hero-tag text-lg md:text-xl mb-12 max-w-xl font-semibold" style={{ color: "#b44fff" }}>
          Scan it. Discover where it's been. Who made it. Who loved it.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          {/* Primary — Join */}
          <button
            onClick={() => navigate("/login?signup=true")}
            className="anim-hero-cta group relative px-8 py-4 md:px-10 md:py-5 rounded-full font-black text-lg md:text-xl text-white overflow-hidden cursor-pointer transition-transform hover:scale-105 active:scale-95"
            style={{
              background: "linear-gradient(135deg, #ff3cac, #b44fff, #00f5ff)",
              boxShadow:  "0 0 40px rgba(180,79,255,0.65), 0 0 80px rgba(255,60,172,0.3)",
            }}
          >
            <span className="relative z-10 flex items-center gap-3">
              Join the Community
              <ArrowRight style={{ width: "1.2rem", height: "1.2rem" }} className="group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>

          {/* Secondary — Log In */}
          <button
            onClick={() => navigate("/login")}
            className="anim-hero-cta px-8 py-4 rounded-full font-bold text-white/70 cursor-pointer hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.05)",
              border:     "1px solid rgba(255,255,255,0.15)",
              fontSize:   "1rem",
            }}
          >
            Log In
          </button>
        </div>

        {/* Live stat pills */}
        <div className="flex flex-wrap justify-center gap-3">
          {STATS.slice(0, 3).map((s) => (
            <div
              key={s.label}
              className="anim-stat-pill px-4 py-2 rounded-full text-sm font-semibold"
              style={{
                background: `${s.color}18`,
                border:     `1px solid ${s.color}40`,
                color:       s.color,
              }}
            >
              <span className="font-black" data-countup={statNumber(s.value)}>{s.value}</span> {s.label}
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-float">
          <div className="w-px h-8 bg-gradient-to-b from-transparent to-white/30" />
          <p className="text-white/30 text-xs tracking-widest uppercase">Scroll</p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
           §2  EXPERIENCE — Feel the Journey
      ════════════════════════════════════════════════════════════════════ */}
      <section id="experience" className="relative py-16 md:py-32 px-4" style={{ zIndex: 10 }}>
        <div className="max-w-6xl mx-auto">

          <div className="anim-exp-head text-center mb-20">
            <h2
              className="mb-6"
              style={{
                fontFamily: "var(--font-display)", fontWeight: 700,
                fontSize:   "clamp(2.5rem, 6vw, 4.5rem)",
                color: "#00f5ff",
                textShadow: "0 0 30px rgba(180,79,255,0.5)",
              }}
            >
              Feel the Journey
            </h2>
            <p className="text-white/55 text-xl max-w-2xl mx-auto">
              From your hands to the world. Every PLUR trade is a chapter in a living story.
            </p>
          </div>

          {/* 3-Step interactive cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {EXPERIENCE_STEPS.map((step, i) => (
              <div
                key={i}
                className="anim-step-card relative rounded-3xl p-8 cursor-pointer transition-all duration-500"
                style={{
                  background:    activeStep === i
                    ? `linear-gradient(135deg, ${step.color}22, rgba(255,255,255,0.04))`
                    : "rgba(255,255,255,0.025)",
                  border:        `1px solid ${activeStep === i ? step.color + "60" : "rgba(255,255,255,0.07)"}`,
                  boxShadow:     activeStep === i ? `0 0 50px ${step.color}28, inset 0 1px 0 rgba(255,255,255,0.08)` : "none",
                  backdropFilter: "blur(24px)",
                  transform:     activeStep === i ? "translateY(-10px)" : "none",
                }}
                onMouseEnter={() => setActiveStep(i)}
              >
                <div className="absolute top-4 right-6 text-7xl font-black text-white/05 select-none">
                  0{i + 1}
                </div>

                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background: step.gradient,
                    boxShadow:  `0 8px 40px ${step.color}55`,
                  }}
                >
                  {step.icon}
                </div>

                <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-white/55 leading-relaxed">{step.desc}</p>

                {activeStep === i && (
                  <div
                    className="absolute bottom-0 left-8 right-8 h-px rounded-full"
                    style={{ background: `linear-gradient(90deg, transparent, ${step.color}, transparent)` }}
                  />
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
           §3  PLUR CULTURE + COMMUNITY
      ════════════════════════════════════════════════════════════════════ */}
      <section id="plur" className="relative py-16 md:py-32 px-4 overflow-hidden" style={{ zIndex: 10 }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 25% 50%, rgba(255,60,172,0.07) 0%, transparent 55%), " +
              "radial-gradient(ellipse at 75% 50%, rgba(0,245,255,0.05) 0%, transparent 55%)",
          }}
        />

        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-20">
            <div
              className="anim-plur-head inline-block mb-5 px-5 py-2 rounded-full text-sm font-bold tracking-widest uppercase"
              style={{
                background: "rgba(255,215,0,0.08)",
                border:     "1px solid rgba(255,215,0,0.28)",
                color:      "#ffd700",
              }}
            >
              Peace · Love · Unity · Respect
            </div>
            <h2
              className="font-black text-white mb-6"
              style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}
            >
              {["PLUR", "is", "More", "Than"].map((w) => (
                <span key={w} className="anim-plur-word" style={{ display: "inline-block", marginRight: "0.26em" }}>{w}</span>
              ))}
              <br />
              {["a", "Saying"].map((w) => (
                <span key={w} className="anim-plur-word" style={{ display: "inline-block", marginRight: "0.26em" }}>{w}</span>
              ))}
            </h2>
            <p className="anim-plur-head text-white/55 text-xl max-w-2xl mx-auto">
              It's a culture. A handshake. A bracelet traded between strangers who become something more.
            </p>
          </div>

          {/* PLUR bubble field */}
          <div className="plur-bubbles relative h-[320px] md:h-[520px] mb-12 md:mb-24">

            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ opacity: 0.45 }}>
              <defs>
                {[
                  ["g1", "#ff3cac", "#b44fff", "0%", "0%", "100%", "0%"],
                  ["g2", "#b44fff", "#00f5ff", "0%", "0%", "100%", "0%"],
                  ["g3", "#ff3cac", "#b44fff", "0%", "0%", "0%",   "100%"],
                  ["g4", "#b44fff", "#00f5ff", "0%", "0%", "0%",   "100%"],
                ].map(([id, c1, c2, x1, y1, x2, y2]) => (
                  <linearGradient key={id} id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
                    <stop offset="0%"   stopColor={c1} />
                    <stop offset="100%" stopColor={c2} />
                  </linearGradient>
                ))}
              </defs>
              {[
                ["M 18 20 Q 50 4 80 22",  "url(#g1)"],
                ["M 22 80 Q 50 96 78 78",  "url(#g2)"],
                ["M 18 20 Q 8  50 22 80",  "url(#g3)"],
                ["M 80 22 Q 92 50 78 78",  "url(#g4)"],
              ].map(([d, stroke], i) => (
                <path key={i} d={d} stroke={stroke} strokeWidth="3" fill="none" strokeLinecap="round" vectorEffect="non-scaling-stroke" className="anim-plur-arc" />
              ))}
            </svg>

            {/* PLUR bubbles */}
            {[
              { label: "PEACE",   icon: <Heart    className="plur-bubble-icon" style={{ width: "4rem", height: "4rem", color: "#fff" }} />, grad: "linear-gradient(135deg,#ff3cac,#ff6b6b)", glow: "#ff3cac", pos: { top: "4%",    left:  "8%"  }, size: 152, delay: "0s"   },
              { label: "LOVE",    icon: <Gift     className="plur-bubble-icon" style={{ width: "4rem", height: "4rem", color: "#fff" }} />, grad: "linear-gradient(135deg,#b44fff,#ff3cac)", glow: "#b44fff", pos: { top: "4%",    right: "8%"  }, size: 168, delay: "0.6s" },
              { label: "UNITY",   icon: <Users    className="plur-bubble-icon" style={{ width: "4rem", height: "4rem", color: "#fff" }} />, grad: "linear-gradient(135deg,#7b2fff,#b44fff)", glow: "#b44fff", pos: { bottom: "8%", left:  "10%" }, size: 168, delay: "1.2s" },
              { label: "RESPECT", icon: <Globe    className="plur-bubble-icon" style={{ width: "4rem", height: "4rem", color: "#fff" }} />, grad: "linear-gradient(135deg,#00f5ff,#0088ff)", glow: "#00f5ff", pos: { bottom: "6%", right: "7%"  }, size: 184, delay: "1.8s" },
            ].map(({ label, icon, grad, glow, pos, size, delay }, bi) => {
              // Beads pin to the bubble's OUTER rim — the side facing away
              // from the nexus, derived from which corner the bubble sits in
              const v = "top" in pos ? "top" : "bottom";
              const h = "left" in pos ? "left" : "right";
              const beadSpots = [
                { [v]: -10, [h]: "16%" },
                { [v]: "24%", [h]: -18 },
              ];
              return (
              <div key={label} className="anim-plur-bubble absolute hover:scale-110 transition-all duration-300 cursor-pointer" style={pos}>
                {/* Rim beads — stagnant on the outer side, popping in after
                    the arcs land. Visible/static when motion is reduced. */}
                {beadSpots.map((spot, k) => (
                  <img
                    key={k}
                    className="plur-bead-pop"
                    aria-hidden
                    src={BEAD_IMAGES[(bi * 2 + k) % BEAD_IMAGES.length]}
                    alt=""
                    style={{
                      position: "absolute", ...spot,
                      width: 24 + bi * 3, height: 24 + bi * 3,
                      objectFit: "contain", pointerEvents: "none", zIndex: 1,
                      filter: `drop-shadow(0 0 6px ${glow}59)`,
                    }}
                  />
                ))}
                <div
                  className="plur-bubble rounded-full flex items-center justify-center animate-float"
                  style={{
                    width:  size,
                    height: size,
                    background: grad,
                    boxShadow:  `0 0 70px ${glow}55, 0 20px 50px rgba(0,0,0,0.45)`,
                    border:     "2px solid rgba(255,255,255,0.2)",
                    animationDelay: delay,
                  }}
                >
                  {icon}
                </div>
                <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-center">
                  <p className="plur-bubble-label text-white font-black text-lg tracking-widest">{label}</p>
                </div>
              </div>
              );
            })}

            {/* Center nexus */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center animate-neon-pulse"
                style={{
                  background: "radial-gradient(circle, rgba(180,79,255,0.25), transparent)",
                  border:     "2px solid rgba(180,79,255,0.55)",
                  boxShadow:  "0 0 40px rgba(180,79,255,0.45)",
                }}
              >
                <Radio style={{ width: "2rem", height: "2rem", color: "#b44fff" }} />
              </div>
            </div>

          </div>

          {/* Stats grid */}
          <div className="anim-stats-grid grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="anim-stat-tile rounded-2xl p-6 text-center cursor-default transition-all duration-300 hover:-translate-y-1"
                style={{
                  background:     `linear-gradient(135deg, ${s.color}12, rgba(255,255,255,0.02))`,
                  border:         `1px solid ${s.color}28`,
                  backdropFilter: "blur(20px)",
                }}
              >
                <div className="text-4xl font-black mb-2" style={{ color: s.color }} data-countup={statNumber(s.value)}>{s.value}</div>
                <div className="text-white/45 text-sm">{s.label}</div>
              </div>
            ))}
          </div>

        </div>
      </section>


      {/* FOOTER */}
      <footer className="relative px-4 py-8" style={{ zIndex: 10, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-3">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span style={{
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.88rem",
              color: "#b44fff",
            }}>KandiKonnect</span>
            <span style={{ color: "rgba(255,255,255,0.15)" }}>·</span>
            <button onClick={() => navigate("/events")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", cursor: "pointer", padding: "4px 6px" }}>Events</button>
            <button onClick={() => navigate("/store")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", cursor: "pointer", padding: "4px 6px" }}>Store</button>
            <button onClick={() => navigate("/about")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", cursor: "pointer", padding: "4px 6px" }}>About</button>
            <button onClick={() => navigate("/contact")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", cursor: "pointer", padding: "4px 6px" }}>Contact</button>
            <a href="https://instagram.com/kandikonnect" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", textDecoration: "none", padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </a>
            <a href="https://x.com/kandikonnect" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", textDecoration: "none", padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X
            </a>
          </div>
          <p style={{ color: "rgba(255,255,255,0.15)", fontSize: "0.7rem", margin: 0 }}>
            © {new Date().getFullYear()} KandiKonnect · Made with PLUR ✌️
          </p>
        </div>
      </footer>

    </div>
  );
}

export { EDM_THEMES };
export default LandingPage;
